import React from 'react'
import {Drawer} from 'rsuite'

function ReportDetailDrawer({ report, open, onClose, onDeleteClick, onDismissClick, onBlockClick }) {
  if (!report) return null;
  
  const getTimeAgo = (postTime) => {
    const now = new Date();
    const postDate = new Date(postTime);
    const diff = now - postDate;
  
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  
    const years = Math.floor(months / 12);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  };

  const blogText = report.blog?.blogText || '';
  const blogImage = report.blog?.blogImage;
  const authorName = report.userFullName || report.blog?.user?.fullName || 'Unknown User';
  const userAvatar = report.userAvatar || report.blog?.user?.avatar;
  const postTime = report.blog?.createdAt ? getTimeAgo(report.blog.createdAt) : '';
  

  const firstReport = report.reportedByInfo && report.reportedByInfo.length > 0 ? report.reportedByInfo[0] : null;
  const reportDate = firstReport?.dateAdded || '';
  const reportReason = firstReport?.reason || 'No reason provided';
  const reportedBy = firstReport?.fullName || 'Unknown Reporter';

  return (
    <Drawer open={open} onClose={onClose} size="md" placement="right" className="custom-drawer">
      <Drawer.Header style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '16px', gap: '8px' }}>
        <Drawer.Title className="font-semibold text-3xl leading-7 tracking-normal">Reported Post</Drawer.Title>
        <p>Please review this post and provide recommended actions</p>
      </Drawer.Header>
      
      <Drawer.Body style={{ height: '70%', padding: '24px', overflowY: 'auto' }}>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 font-medium text-gray-700">Date</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Report Reason</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Status</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Posted by</th>
                <th className="text-left py-2 px-2 font-medium text-gray-700">Reported By</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-3 px-2">{reportDate ? new Date(reportDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</td>
                <td className="py-3 px-2">{reportReason}</td>
                <td className="py-3 px-2">
                  <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs flex items-center w-fit">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                    Pending Review
                  </span>
                </td>
                <td className="py-3 px-2">{authorName}</td>
                <td className="py-3 px-2">{reportedBy}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Post Content */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          {/* User Info */}
          <div className="flex items-start mb-4">
            <div className="w-12 h-12 bg-gray-300 rounded-full mr-3 overflow-hidden">
              {userAvatar ? (
                <img src={userAvatar} alt={authorName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-400 text-white font-semibold">
                  {authorName?.charAt(0) || 'U'}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{authorName}</h4>
              <p className="text-gray-500 text-sm flex items-center">
                {postTime}
                <span className="ml-1">🌍</span>
              </p>
            </div>
          </div>

          {/* Post Text */}
          <div className="mb-4">
            <div
              className="text-gray-800 leading-relaxed mb-2"
              dangerouslySetInnerHTML={{ __html: blogText }}
            />
            {/* Hashtags */}
            <div className="flex flex-wrap gap-1">
              <span className="text-blue-600 hover:text-blue-800 cursor-pointer">#hashtag</span>
              <span className="text-blue-600 hover:text-blue-800 cursor-pointer">#hashtag</span>
              <span className="text-blue-600 hover:text-blue-800 cursor-pointer">#hashtag</span>
            </div>
          </div>

          {/* Post Image/Video */}
          {blogImage && (
            <div className="mb-4 relative">
              <img 
                src={blogImage} 
                alt="Post content" 
                className="w-full rounded-lg"
                style={{ maxHeight: '400px', objectFit: 'cover' }}
              />
            </div>
          )}
          {/* Engagement Stats */}
        </div>
      </Drawer.Body>

      <Drawer.Actions style={{ padding: '10px 10px', height: '76px' }}>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex">Quick Actions</h3>
          <div className="flex gap-3">
            <button
              onClick={onDeleteClick}
              className="bg-red-100 hover:bg-red-50 text-red-500 px-3 py-1 rounded report-action-btn"
            >
              Delete Post
            </button>
            <button
              onClick={onBlockClick}
              className="border border-red-500 text-red-500 px-3 py-1 rounded hover:bg-red-50 report-action-btn"
            >
              Block User
            </button>
            <button
              onClick={onDismissClick}
              className="bg-teal-500 hover:bg-teal-600 text-white px-3 py-1 rounded report-action-btn"
            >
              Dismiss Report
            </button>
          </div>
        </div>
      </Drawer.Actions>
    </Drawer>
  );
}

export default ReportDetailDrawer
